import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ChallengeCard, { challengeCardBtn } from '../../ChallengeWire/ChallengeCard';
import ChallengeWireChallengeSections from '../../ChallengeWire/ChallengeWireChallengeSections';
import HonorCodeModal from '../../ChallengeWire/HonorCodeModal';
import FitnessProfileSectionCard from '../shared/FitnessProfileSectionCard';
import { groupChallengesForAllView } from '../../../utils/challengePerspective';
import { celebrateChallengeComplete } from '../../../utils/celebrationConfetti';
import { honorCodeFirstName } from '../../../constants/honorCode';

import ChallengeResolveActions from '../../ChallengeWire/ChallengeResolveActions';
import { challengeHasActions } from '../../../hooks/useChallengeActions';

function honorModalProps(challenge) {
	if (!challenge) return { title: 'Accept challenge' };
	if (challenge.kind === 'household' && challenge.challenged_profile?.name) {
		return {
			acceptingAsName: honorCodeFirstName(challenge.challenged_profile.name),
			challengeTitle: challenge.title
		};
	}
	return { title: `Accept: ${challenge.title}` };
}

export default function FitnessProfileChallengesSection({
	isPrimary,
	profileName,
	challenges,
	loading,
	error,
	actionError,
	onClearActionError,
	onRunAction,
	onDayCheck,
	isDayCheckPending
}) {
	const [honorChallenge, setHonorChallenge] = useState(null);

	const subtitle = isPrimary
		? 'Household challenges for this profile and friend challenges on your account.'
		: 'Household challenges where this profile is involved.';

	function renderChallengeActions(challenge) {
		if (!challengeHasActions(challenge)) return null;

		return (
			<>
				{challenge.can_accept && (
					<>
						<button
							type="button"
							onClick={() => {
								onClearActionError();
								setHonorChallenge(challenge);
							}}
							className={challengeCardBtn.primary}
						>
							Accept
						</button>
						<button
							type="button"
							onClick={() => onRunAction(challenge, 'decline').catch(() => {})}
							className={challengeCardBtn.secondary}
						>
							Decline
						</button>
					</>
				)}
				{challenge.can_fail && (
					<ChallengeResolveActions
						challenge={challenge}
						onComplete={() =>
							onRunAction(challenge, 'complete')
								.then(() => celebrateChallengeComplete())
								.catch(() => {})
						}
						onFail={() => onRunAction(challenge, 'fail').catch(() => {})}
					/>
				)}
				{challenge.can_cancel && (
					<span className={challenge.can_accept || challenge.can_fail ? 'ml-auto' : ''}>
						<button
							type="button"
							onClick={() => onRunAction(challenge, 'cancel').catch(() => {})}
							className={challengeCardBtn.ghost}
						>
							Cancel
						</button>
					</span>
				)}
			</>
		);
	}

	function renderChallengeGrid(items) {
		return (
			<ul className="grid grid-cols-1 lg:grid-cols-2 gap-4">
				{items.map((challenge) => (
					<li key={`${challenge.kind}-${challenge.id}`} className="h-full min-h-0">
						<ChallengeCard
							challenge={challenge}
							onDayCheck={onDayCheck}
							isDayCheckPending={isDayCheckPending}
						>
							{renderChallengeActions(challenge)}
						</ChallengeCard>
					</li>
				))}
			</ul>
		);
	}

	const headerAction = (
		<div className="flex flex-wrap items-center gap-2 shrink-0">
			<Link
				to="/challenge-wire"
				className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
			>
				Challenge Wire
			</Link>
			{isPrimary && (
				<Link
					to="/challenge-wire"
					state={{ openSend: true }}
					className="inline-flex items-center rounded-xl bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 dark:hover:bg-blue-500 transition"
				>
					+ New
				</Link>
			)}
		</div>
	);

	return (
		<div className="mt-4">
			<FitnessProfileSectionCard title="Current challenges" subtitle={subtitle} headerAction={headerAction}>
				{(error || actionError) && (
					<p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
						{actionError || error}
					</p>
				)}

				{loading ? (
					<div className="flex items-center gap-3 py-8 text-sm text-gray-500 dark:text-gray-400">
						<span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 dark:border-gray-600 dark:border-t-blue-400" />
						Loading challenges…
					</div>
				) : challenges.length === 0 ? (
					<div className="rounded-xl border border-dashed border-gray-300 dark:border-dark-border bg-gray-50 dark:bg-dark-bg px-5 py-8 text-center">
						<p className="text-sm font-medium text-gray-800 dark:text-gray-200">No active challenges right now</p>
						<p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
							{isPrimary
								? 'Start a friend challenge or a household challenge between profiles on your account.'
								: `${profileName || 'This profile'} has no household challenges in progress.`}
						</p>
						{isPrimary && (
							<Link
								to="/challenge-wire"
								state={{ openSend: true }}
								className="mt-4 inline-flex items-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition"
							>
								Send a challenge
							</Link>
						)}
					</div>
				) : (
					<ChallengeWireChallengeSections
						sections={groupChallengesForAllView(challenges)}
						renderGrid={renderChallengeGrid}
					/>
				)}
			</FitnessProfileSectionCard>

			<HonorCodeModal
				isOpen={!!honorChallenge}
				onClose={() => setHonorChallenge(null)}
				onConfirm={() =>
					onRunAction(honorChallenge, 'accept')
						.then(() => setHonorChallenge(null))
						.catch(() => {})
				}
				{...honorModalProps(honorChallenge)}
			/>
		</div>
	);
}
